import { createMemo, For, Show } from "solid-js";
import HeaderBackButton from "./header/HeaderBackButton";
import HeaderSaveButton from "./header/HeaderSaveButton";
import HeaderPublishButton from "./header/HeaderPublishButton";
import HeaderPreviewButton from "./header/HeaderPreviewButton";
import HeaderVersionHistoryButton from "./header/HeaderVersionHistoryButton.tsx";
import StopImpersonationButton from "../StopImpersonationButton";
import SeparatorLine from "../../components/SeparatorLine";
import HeaderLink from "../../components/link/HeaderLink";
import { FormEditorRoute } from "./FormEditorContent";
import HelpMenu from "../customer/layout/header/HelpMenu";
import { IconButton } from "../../components/button/Button";
import { useNavigate } from "@solidjs/router";
import HeaderValidationButton from "./header/HeaderValidationButton";
import { useFormServices } from "../../services/formServices";
import { useTranslation } from "../../state/i18n";
import VariableService from "../../services/VariableService";

type FormEditorHeaderProps = {
  activeItem: FormEditorRoute;
};

const FormEditorHeader = (props: FormEditorHeaderProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const mainLinks = [
    { label: t(($) => $.FORM_EDITOR_LINK_EDITOR), key: FormEditorRoute.FIELDS },
    { label: t(($) => $.FORM_EDITOR_LINK_SUBFORMS), key: FormEditorRoute.SUBFORMS },
    { label: t(($) => $.FORM_EDITOR_LINK_RULES), key: FormEditorRoute.RULES },
    { label: t(($) => $.FORM_EDITOR_LINK_INTEGRATIONS), key: FormEditorRoute.INTEGRATIONS },
  ];

  const { validationService } = useFormServices();
  const showValidationButton = createMemo(validationService.hasErrors);
  const showVariables = createMemo(VariableService.showVariables);

  return (
    <header class="flex max-w-full items-center justify-between border-b border-subtle bg-surface-default py-1.5 pr-3 pl-1">
      <div class="flex h-7.5 items-center gap-x-2">
        <HeaderBackButton />
        <SeparatorLine orientation="vertical" class="h-full" />
        <div class="flex gap-x-2 pl-2">
          <For each={mainLinks}>
            {(link) => (
              <HeaderLink as="link" label={link.label} active={props.activeItem === link.key} href={`../${link.key}`} />
            )}
          </For>
          <Show when={showVariables()}>
            <HeaderLink
              as="link"
              label={t(($) => $.FORM_EDITOR_LINK_VARIABLES)}
              active={props.activeItem === FormEditorRoute.VARIABLES}
              href={`../${FormEditorRoute.VARIABLES}`}
            />
          </Show>
        </div>
      </div>
      <div class="flex h-9.5">
        <div class="flex gap-x-2">
          <StopImpersonationButton />

          <Show when={showValidationButton()}>
            <HeaderValidationButton activeItem={props.activeItem} />
          </Show>

          <IconButton
            aria-label={t(($) => $.FORM_EDITOR_LINK_SETTINGS)}
            icon="cog"
            iconType="outline"
            size="large"
            variant="secondary"
            onClick={() => navigate(`../${FormEditorRoute.SETTINGS}`)}
          />
        </div>
        <SeparatorLine orientation="vertical" class="mx-3 h-full" />
        <div class="flex items-center gap-2">
          <HeaderVersionHistoryButton />
          <HeaderPreviewButton />
          <HeaderSaveButton />
          <HeaderPublishButton />
          <HelpMenu triggerSize="large" showExitBeta={false} />
        </div>
      </div>
    </header>
  );
};

export default FormEditorHeader;
